import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import type { ApiResponse, TokenPair, Customer, CustomerProfileUpdate, CustomerReportSummary, CustomerReportDetail, CustomerTokenPair, User, Business, BusinessCandidate, ScanSession, ProgressEvent, ReportPayload, KeywordDetailData } from '../types'
import type { SiteSchema, Page, Asset } from '../site-builder/schema'

// ── Axios instance ────────────────────────────────────────────────────────────
const api: AxiosInstance = axios.create({
  baseURL: '/v1',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

// Request interceptor: attach access token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Response interceptor: handle 401 by attempting token refresh.
// Customer sessions use /auth/customer/refresh; admin sessions use /auth/refresh.
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config as AxiosRequestConfig & { _retry?: boolean; skipAuthRedirect?: boolean }
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true
      const refreshToken = localStorage.getItem('refresh_token')
      const sessionType = localStorage.getItem('session_type') // 'customer' | 'admin'
      if (refreshToken) {
        try {
          const refreshUrl = sessionType === 'admin'
            ? '/v1/auth/refresh'
            : '/v1/auth/customer/refresh'
          const { data } = await axios.post<ApiResponse<TokenPair>>( refreshUrl, {
            refresh_token: refreshToken,
          })
          const tokens = data.data!
          localStorage.setItem('access_token', tokens.access_token)
          localStorage.setItem('refresh_token', tokens.refresh_token)
          api.defaults.headers.common.Authorization = `Bearer ${tokens.access_token}`
          return api(original)
        } catch {
          // The session is fully dead (refresh token expired/invalid).
          localStorage.clear()
          // Callers on public pages (e.g. the report page's session check) opt
          // out of the hard redirect so the visitor stays put and is offered the
          // register/OTP modal instead of being bounced to /login.
          if (!original.skipAuthRedirect) {
            window.location.href = '/login'
          }
        }
      }
    }
    return Promise.reject(err)
  },
)

// ── Helper to unwrap data envelope ───────────────────────────────────────────
// `skipAuthRedirect` is a custom flag read by the 401 interceptor to suppress the
// hard redirect to /login (used by public pages that handle "logged out" locally).
type ReqConfig = AxiosRequestConfig & { skipAuthRedirect?: boolean }

async function req<T>(config: ReqConfig): Promise<T> {
  const { data } = await api(config)
  return (data as ApiResponse<T>).data as T
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authApi = {
  register: (email: string, password: string, firstName: string, lastName: string) =>
    req<User>({ method: 'POST', url: '/auth/register', data: { email, password, first_name: firstName, last_name: lastName } }),

  login: (email: string, password: string) =>
    req<TokenPair & { user_id: string }>({ method: 'POST', url: '/auth/login', data: { email, password } }),

  me: () => req<User>({ method: 'GET', url: '/auth/me' }),

  logout: (refreshToken: string) =>
    api.post('/auth/logout', { refresh_token: refreshToken }),

  refresh: (refreshToken: string) =>
    req<TokenPair>({ method: 'POST', url: '/auth/refresh', data: { refresh_token: refreshToken } }),
}

// ── Businesses ────────────────────────────────────────────────────────────────

/** One page of business search results plus the token for the next page. */
export interface BusinessSearchPage {
  candidates: BusinessCandidate[]
  /** Empty when there are no further pages. */
  nextPageToken: string
}

export const businessApi = {
  /**
   * Search for business candidates. Pass `pageToken` (from a previous page's
   * `nextPageToken`) to fetch the next page for infinite scroll.
   */
  search: async (query: string, city: string, pageToken = ''): Promise<BusinessSearchPage> => {
    const { data } = await api.get<ApiResponse<BusinessCandidate[]> & { meta?: { next_page_token?: string } }>(
      '/business-search',
      { params: { query, city, page_token: pageToken || undefined } },
    )
    return {
      candidates: data.data ?? [],
      nextPageToken: data.meta?.next_page_token ?? '',
    }
  },

  searchByUrl: (url: string) =>
    req<BusinessCandidate[]>({ method: 'GET', url: '/business-search', params: { url } }),

  selectCandidate: (candidate: Omit<BusinessCandidate, 'photo_url' | 'is_verified' | 'review_count' | 'rating'>) =>
    req<Business>({ method: 'POST', url: '/business-search/select', data: candidate }),

  list: () => req<Business[]>({ method: 'GET', url: '/businesses' }),

  get: (id: string) => req<Business>({ method: 'GET', url: `/businesses/${id}` }),

  create: (data: Partial<Business>) => req<Business>({ method: 'POST', url: '/businesses', data }),
}

// ── Scans ─────────────────────────────────────────────────────────────────────
export const scanApi = {
  create: (businessId: string, lang: string = 'en') =>
    req<{ scan_id: string; status: string; business_id: string; created_at: string }>({
      method: 'POST', url: '/scans', data: { business_id: businessId, lang },
    }),

  get: (scanId: string) => req<ScanSession>({ method: 'GET', url: `/scans/${scanId}` }),

  list: (businessId: string) => req<ScanSession[]>({ method: 'GET', url: `/scans/business/${businessId}` }),

  getReport: (scanId: string) => req<ReportPayload>({ method: 'GET', url: `/scans/${scanId}/report` }),

  getKeywordDetail: (scanId: string, keyword: string, lang = 'en') =>
    req<KeywordDetailData>({ method: 'GET', url: `/scans/${scanId}/keywords/${encodeURIComponent(keyword)}/detail?lang=${lang}` }),

  streamProgress: (scanId: string, onEvent: (ev: ProgressEvent) => void, onDone?: () => void) => {
    const token = localStorage.getItem('access_token')
    const url = `/v1/scans/${scanId}/progress`

    let es: EventSource | null = null
    let closed = false
    let retries = 0
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    const MAX_RETRIES = 10

    function connect() {
      if (closed) return

      const fullUrl = token ? `${url}?token=${token}` : url
      es = new EventSource(fullUrl)

      es.onmessage = (e) => {
        retries = 0 // reset backoff counter on any successful message
        try {
          const ev: ProgressEvent = JSON.parse(e.data)
          onEvent(ev)
        } catch { /* ignore parse errors */ }
      }

      es.addEventListener('done', () => {
        closed = true
        es?.close()
        onDone?.()
      })

      // On error: close the current connection and schedule a reconnect
      // with exponential back-off (1 s → 2 s → 4 s … capped at 16 s).
      // This handles the case where the server's write-timeout fires before
      // the scan completes — the reconnect will replay all past events and
      // resume live streaming.
      es.onerror = () => {
        es?.close()
        if (closed || retries >= MAX_RETRIES) return
        retries++
        const delay = Math.min(1_000 * Math.pow(2, retries - 1), 16_000)
        retryTimer = setTimeout(connect, delay)
      }
    }

    connect()

    return () => {
      closed = true
      if (retryTimer !== null) clearTimeout(retryTimer)
      es?.close()
    }
  },
}

// ── Brands (Brandfetch proxy) ─────────────────────────────────────────────────
export interface BrandData {
  logo_url: string
  icon_url: string
  primary_color: string
  brand_name: string
  domain: string
  description: string
}

export const brandsApi = {
  /**
   * Fetches brand data (logo, colors) for the given domain via our backend
   * Brandfetch proxy. The backend holds the server-side API key securely.
   * Returns null on any failure so callers can degrade gracefully.
   */
  getBrand: async (domain: string): Promise<BrandData | null> => {
    try {
      // Normalise: strip protocol/www/path so the domain is clean
      const clean = domain
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .toLowerCase()
      if (!clean) return null
      return await req<BrandData>({ method: 'GET', url: `/brands/${encodeURIComponent(clean)}` })
    } catch {
      return null
    }
  },
}

// ── Customer Auth (OTP-based) ─────────────────────────────────────────────────
export const customerAuthApi = {
  /**
   * Step 1: send an OTP to the phone number.
   * When `requireExisting` is true (login flow), the request fails if the phone
   * number is not already registered, instead of creating a new customer.
   * Includes country_code (ISO) and country (name) for international support.
   * Optional firstName, lastName, email are used to update the customer before OTP.
   */
  requestOTP: (phone: string, countryCode: string, country: string, requireExisting = false, firstName = '', lastName = '', email = '') =>
    api.post('/auth/otp/request', { phone, country_code: countryCode, country, require_existing: requireExisting, first_name: firstName, last_name: lastName, email }),

  /** Step 2: verify the OTP and receive tokens. Includes country information. */
  verifyOTP: (phone: string, code: string, countryCode: string, country: string) =>
    req<CustomerTokenPair>({ method: 'POST', url: '/auth/otp/verify', data: { phone, code, country_code: countryCode, country } }),

  /** Refresh customer access token. */
  refresh: (refreshToken: string) =>
    req<CustomerTokenPair>({ method: 'POST', url: '/auth/customer/refresh', data: { refresh_token: refreshToken } }),

  /** Logout — revoke refresh token. */
  logout: (refreshToken: string) =>
    api.post('/auth/customer/logout', { refresh_token: refreshToken }),

  /**
   * Get authenticated customer profile.
   * Pass `{ skipAuthRedirect: true }` on public pages (e.g. the report gate) so a
   * dead session resolves as "not logged in" instead of hard-redirecting to /login.
   */
  me: (opts?: { skipAuthRedirect?: boolean }) =>
    req<Customer>({ method: 'GET', url: '/auth/customer/me', skipAuthRedirect: opts?.skipAuthRedirect }),

  /**
   * Persist the post-verification details wizard (firstName, lastName, email + relationship/
   * consent) for the authenticated customer. Returns the updated profile.
   */
  updateProfile: (data: CustomerProfileUpdate) =>
    req<Customer>({ method: 'POST', url: '/customers/me/profile', data }),

  /** Submit lead-capture form (public — no auth needed). */
  captureLead: (firstName: string, lastName: string, email: string, phone: string, scanId?: string) =>
    req<{ id: string; phone: string; first_name: string; last_name: string }>({
      method: 'POST',
      url: '/customers/capture',
      data: { first_name: firstName, last_name: lastName, email, phone, scan_id: scanId ?? '' },
    }),

  /** List the authenticated customer's report history (newest first). */
  listReports: (page = 1, perPage = 50) =>
    req<CustomerReportSummary[]>({
      method: 'GET',
      url: '/customers/me/reports',
      params: { page, per_page: perPage },
    }),

  /**
   * Get a specific persisted report for the authenticated customer. The full
   * report payload lives under the `report` field (same shape as the public
   * scan report), so it can be rendered with the standard report UI.
   */
  getReport: (reportId: string) =>
    req<CustomerReportDetail>({ method: 'GET', url: `/customers/me/reports/${reportId}` }),

  /**
   * Attach an existing scan session to the authenticated customer so the
   * admin dashboard can associate reports with customers.  Idempotent.
   */
  attachScan: (scanId: string) =>
    api.post(`/customers/me/scans/${encodeURIComponent(scanId)}/attach`),
}

// ── Site Builder (AI website generation — backend-owned) ──────────────────────
// The frontend NEVER generates or stores a schema. It POSTs normalized business
// facts, the Go backend runs the full pipeline (mock or live provider) and PERSISTS
// the result, then the frontend fetches the builder by id and renders it. Refresh,
// new tabs, and dev-server restarts all read from the backend (the DB is the source
// of truth).

/** Normalized business facts sent to the backend generator (its SourceInput shape). */
export interface SiteBuilderSource {
  name: string
  category?: string
  categories?: string[]
  tagline?: string
  shortDescription?: string
  description?: string
  offerings?: { name: string; description?: string }[]
  contact?: { phone?: string; email?: string; website?: string }
  address?: string
  brandColors?: string[]
  socialLinks?: { platform: string; url: string }[]
  reviews?: { author?: string; rating?: number; text: string }[]
  averageRating?: number
  reviewCount?: number
  media?: { logoUrl?: string; coverUrl?: string; galleryUrls?: string[] }
  attributes?: string[]
  priceLevel?: number
  seo?: { keywords?: string[] }
}

/** Result of POST /site-builder/generate. */
export interface GenerateBuilderResult {
  builderId: string
  status: string
}

/**
 * Manual-edit payload for PATCH /site-builder/builders/:id. Theme/branding/SEO/
 * businessInfo are merged per-field (a field omitted here means "leave unchanged" on the
 * backend). `pages`/`assets`, when included, REPLACE the stored section content and asset
 * library wholesale with the editor's current full working copy — this is how per-section
 * edits (nav links, headings, images, list items, …) persist across reload instead of
 * silently reverting; the backend never regenerates or reconstructs them from business data.
 */
export interface BuilderUpdate {
  theme?: {
    designSystem?: string
    primaryColor?: string
    typography?: { headingFont?: string; bodyFont?: string }
    borderRadius?: string
    spacing?: string
  }
  brandColor?: string
  seoTitle?: string
  seoDescription?: string
  /** A data:image/* or http(s) URL to set the logo/favicon, or "" to clear it. */
  logo?: string
  favicon?: string
  logoSize?: number
  businessInfo?: {
    email?: string
    phone?: string
    street?: string
    city?: string
    state?: string
    zip?: string
    unit?: string
    social?: {
      facebook?: string
      instagram?: string
      x?: string
      linkedin?: string
      tiktok?: string
      youtube?: string
    }
  }
  /** The editor's full current pages/sections — replaces the stored ones wholesale. */
  pages?: readonly Page[]
  /** The editor's full current asset library — replaces the stored one wholesale. */
  assets?: readonly Asset[]
}

/** A persisted builder record from GET /site-builder/builders/:id. */
export interface BuilderRecord {
  id: string
  businessId: string
  status: string
  locale: string
  provider: string
  model: string
  promptVersion: string
  schema: SiteSchema | null
  diagnostics?: unknown
  errorMessage?: string
  createdAt: string
  updatedAt: string
}

export const siteBuilderApi = {
  /**
   * Generate a website. The backend keeps ONE active builder per business: without
   * `regenerate` an existing builder is reused as-is; with `regenerate: true` the
   * pipeline re-runs and overwrites that builder in place (same id).
   */
  generate: (payload: { source: SiteBuilderSource; businessId?: string; locale?: string; regenerate?: boolean }) =>
    req<GenerateBuilderResult>({ method: 'POST', url: '/site-builder/generate', data: payload }),

  /** Fetch a persisted builder (schema + metadata) by id — the single source of truth. */
  getBuilder: (id: string) =>
    req<BuilderRecord>({ method: 'GET', url: `/site-builder/builders/${id}` }),

  /**
   * Manual edit. Persists Design (theme/brand color), Settings (SEO/businessInfo), and
   * Sections (pages/assets) in place — same id, no regeneration. Returns the updated
   * record. The DB remains the single source of truth, so a refresh restores exactly
   * these saved values.
   */
  updateBuilder: (id: string, patch: BuilderUpdate) =>
    req<BuilderRecord>({ method: 'PATCH', url: `/site-builder/builders/${id}`, data: patch }),

  /** The business's active builder, or null when none has been generated yet. */
  getBuilderForBusiness: async (businessId: string): Promise<BuilderRecord | null> => {
    try {
      return await req<BuilderRecord>({
        method: 'GET',
        url: `/site-builder/businesses/${businessId}/builder`,
        skipAuthRedirect: true,
      })
    } catch (e) {
      // No builder yet (404) or not authenticated on a public report (401) → treat as none.
      if (axios.isAxiosError(e) && (e.response?.status === 404 || e.response?.status === 401)) return null
      throw e
    }
  },
}

export default api
